import {
  ApiResponse,
  Currency,
  DirectionEnum,
  User,
} from '../models';
import http from '../utils/http';
import { UserRolesEnum } from '../enums';
import {
  decamelize,
  decamelizeKeys,
} from 'humps';
import { NotesResponse } from '../models/responses/users.model';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed.filter(
          (backend: BackendAuthToken) =>
            backend &&
            backend.name &&
            backend.url &&
            backend.token,
        )
      : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string,
): BackendAuthToken {
  const backends = getBackendAuthTokens();

  const backend = backends.find(
    (item) => item.name === backendName,
  );

  if (!backend) {
    throw new Error(
      `[USERS] Backend not found: ${backendName}`,
    );
  }

  return backend;
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string,
): string {
  return `${backend.url.replace(/\/+$/, '')}${path}`;
}

function getBackendConfig(
  backend: BackendAuthToken,
): any {
  return {
    withCredentials: true,
    backend: backend.name,
    headers: {
      'Auth-Token': backend.token,
    },
  };
}

/**
 * GET ALL MANAGERS
 *
 * Dohvata managere sa svih autentifikovanih backenda.
 */
export async function serviceFetchAllManagers(): Promise<User[]> {
  return serviceFetchAllUsersByRole(
    UserRolesEnum.MANAGER,
  );
}

/**
 * GET USERS BY ROLE
 *
 * Dohvata korisnike određene role sa svih backenda.
 */
export async function serviceFetchAllUsersByRole(
  roleId: number,
): Promise<User[]> {
  const backends = getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response: ApiResponse<User[]> =
          await http.get(
            getBackendUrl(
              backend,
              `/api/users?id_role=${roleId}`,
            ),
            getBackendConfig(backend),
          );

        const users = Array.isArray(response.data)
          ? response.data
          : [];

        return users.map((user: any) => ({
          ...user,
          backendName: backend.name,
          backendUrl: backend.url,
        }));
      } catch (error) {
        console.error(
          `[USERS] ${backend.name} users-by-role request failed`,
          error,
        );

        return [];
      }
    }),
  );

  return responses.flat();
}

/**
 * GET CURRENCIES
 *
 * Ako valute postoje na više backenda,
 * vraća ih objedinjene bez duplikata.
 */
export async function serviceFetchAllCurrencies(): Promise<Currency[]> {
  const backends = getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response: ApiResponse<Currency[]> =
          await http.get(
            getBackendUrl(
              backend,
              '/api/users/constant/currencies',
            ),
            getBackendConfig(backend),
          );

        return Array.isArray(response.data)
          ? response.data
          : [];
      } catch (error) {
        console.error(
          `[USERS] ${backend.name} currencies request failed`,
          error,
        );

        return [];
      }
    }),
  );

  const currencies: Currency[] = [];

  responses.flat().forEach((currency: Currency) => {
    const exists = currencies.some(
      (item: any) =>
        item.id === (currency as any).id ||
        item.code === (currency as any).code,
    );

    if (!exists) {
      currencies.push(currency);
    }
  });

  return currencies;
}

/**
 * BLOCK USER
 *
 * Mora dobiti backendName korisnika.
 */
export async function serviceBlockUser(
  usernameOrEmail: string,
  action: boolean,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/users/set-block',
      ),
      {
        usernameOrEmail,
        action,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          ...getBackendConfig(backend).headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * GET SUBTREE USERS
 */
export async function serviceGetSubtreeUsers(
  params?: any,
): Promise<User[]> {
  const backends = getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response: ApiResponse<User[]> =
          await http.get(
            getBackendUrl(
              backend,
              '/api/users/subtree',
            ),
            {
              ...getBackendConfig(backend),
              params,
            },
          );

        const users = Array.isArray(response.data)
          ? response.data
          : [];

        return users.map((user: any) => ({
          ...user,
          backendName: backend.name,
          backendUrl: backend.url,
        }));
      } catch (error) {
        console.error(
          `[USERS] ${backend.name} subtree request failed`,
          error,
        );

        return [];
      }
    }),
  );

  return responses.flat();
}

/**
 * SEARCH USERS
 */
export async function serviceSearchUsers(
  params: any,
): Promise<any> {
  const backends = getBackendAuthTokens();

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response: ApiResponse<any> =
          await http.get(
            getBackendUrl(
              backend,
              '/api/users',
            ),
            {
              ...getBackendConfig(backend),
              params,
            },
          );

        const data = response.data;

        if (
          data &&
          Array.isArray(data.data)
        ) {
          return {
            ...data,
            data: data.data.map(
              (user: any) => ({
                ...user,
                backendName: backend.name,
                backendUrl: backend.url,
              }),
            ),
          };
        }

        if (Array.isArray(data)) {
          return data.map(
            (user: any) => ({
              ...user,
              backendName: backend.name,
              backendUrl: backend.url,
            }),
          );
        }

        return data;
      } catch (error) {
        console.error(
          `[USERS] ${backend.name} search request failed`,
          error,
        );

        return null;
      }
    }),
  );

  const validResponses =
    responses.filter(
      (response) => response !== null,
    );

  if (
    validResponses.length === 1
  ) {
    return validResponses[0];
  }

  const users: any[] = [];
  let rowcount = 0;

  validResponses.forEach(
    (response: any) => {
      if (
        Array.isArray(response?.data)
      ) {
        users.push(...response.data);

        rowcount +=
          response.rowcount ||
          response.rowCount ||
          response.data.length ||
          0;
      } else if (
        Array.isArray(response)
      ) {
        users.push(...response);

        rowcount += response.length;
      }
    },
  );

  return {
    data: users,
    rowcount,
  };
}

/**
 * PUT USER ON RISK
 */
export async function servicePutOnRisk(
  username: string,
  rules: any,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/risk/user',
      ),
      {
        username,
        rules,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          ...getBackendConfig(backend).headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * RESET USER RISK
 */
export async function serviceResetRiskOnUser(
  username: string,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/risk/user/reset',
      ),
      {
        username,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          ...getBackendConfig(backend).headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * SET USER PROPERTY
 */
export async function serviceSetUserProperty(
  username: string,
  paymentLimit?: number,
  agentPaymentPercent?: number,
  enableDirectPayment: boolean = true,
  backendName?: string,
): Promise<any> {
  let backend: BackendAuthToken;

  if (backendName) {
    backend = getBackend(backendName);
  } else {
    const backends = getBackendAuthTokens();

    if (!backends.length) {
      throw new Error(
        '[USERS] No authenticated backend',
      );
    }

    backend = backends[0];
  }

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/master/user/child/property/set',
      ),
      {
        username,
        paymentLimit,
        agentPaymentPercent,
        enableDirectPayment,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          ...getBackendConfig(backend).headers,
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * SUBTREE USERS V2
 *
 * Ovo ti je već bilo multi-backend.
 * Ostavio sam ga i sredio da dodaje backendUrl.
 */
export async function serviceSubtreeUsersV2(
  params?: { [key: string]: any },
  orderBy?: string,
  direction?: DirectionEnum,
): Promise<any> {
  const reqData: {
    [key: string]: any;
  } = {
    ...params,
  };

  if (
    params?.idRoleSearch !== undefined
  ) {
    reqData.id_role_search =
      params.idRoleSearch;

    delete reqData.idRoleSearch;
  }

  if (params?.withDetails) {
    reqData.limit = 50;
    reqData.page = 0;
    reqData.isPagination = true;
  }

  if (orderBy) {
    reqData.orderBy =
      decamelize(orderBy);

    reqData.direction = direction;
  }

  const backends =
    getBackendAuthTokens();



  const responses =
    await Promise.all(
      backends.map(
        async (backend) => {
          const url =
            getBackendUrl(
              backend,
              '/api/users/subtree/v2',
            );

          try {

            const response:
              ApiResponse<User[]> =
              await http.get(
                url,
                {
                  params: reqData,
                  ...getBackendConfig(
                    backend,
                  ),
                },
              );

            const data =
              Array.isArray(
                response.data,
              )
                ? response.data
                : [];

            return data.map(
              (user: any) => ({
                ...user,
                backendName:
                  backend.name,
                backendUrl:
                  backend.url,
              }),
            );
          } catch (error) {
            console.error(
              `[USERS] ${backend.name} request failed`,
              error,
            );

            return [];
          }
        },
      ),
    );

  const users = responses.flat();

  return users;
}

/**
 * NOTES + STATUS
 */
export async function getNotesAndStatusForUser(
  usernameOrEmail: string,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<NotesResponse> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/users/notes/status',
      ),
      {
        ...getBackendConfig(backend),
        params: {
          username_or_email: usernameOrEmail,
        },
      },
    );

  return response.data;
}

/**
 * ADD NOTE
 */
export async function addNewNoteOnUser(
  idUser: number,
  note: string,
  backendName: string,
  type?: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const data: any = {
    idUser,
    note,
  };

  if (type) {
    data.type = type;
  }

  const response = await http.post(
    getBackendUrl(backend, '/api/users/notes'),
    data,
    {
      ...getBackendConfig(backend),
      headers: {
        ...getBackendConfig(backend).headers,
        successAlert: true,
      },
    },
  );

  return response.data;
}

/**
 * DELETE NOTE
 */
export async function deleteNote(
  idNote: number,
  backendName: string,
): Promise<any> {
  const backend =
    getBackend(backendName);

  const response: ApiResponse<any> =
    await http.delete(
      getBackendUrl(
        backend,
        '/api/users/notes',
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idNote,
        },
      },
    );

  return response.data;
}